<template>
  <div class="lp-element-panel lp-logo-panel">
    <field-group label="Logo color">
      <segmented-control
          :options="logoColorOptions"
          :value="logoColorValue"
          @click="onLogoColorClick"/>
    </field-group>

    <template v-if="isMultiPropertySelection || getSelectedObjectsList.length == 1">
      <fill-color-section
          :open="fillOpen"
          title="Fill color"
          @toggle="$emit('toggle-fill')">
        <color-picker
            :color="getColorLabelShape"
            :type="getObjectType"
            :key="'color-picker-'+((activeObjectForLeftPanel && activeObjectForLeftPanel.Id) || 'none')+'-'+((activeObjectForLeftPanel && activeObjectForLeftPanel.isGradient) || false)+'-'+getSelectedPointOfGradient"
            :active-object="activeObjectForLeftPanel"
            :selected-objects-list="getSelectedObjectsList"
            :content-list="contentList"
            display-style="inline"
            variant="panel"
            color-type="fill"
            @updateSelectedPointOfGradient="updateSelectedPointOfGradient"
            @updateSelectedPointPosition="updateSelectedPointPosition"
            @changeMode="handleModeChange"
            @updateSelectedGradientAngle="updateSelectedGradientAngle"
        />
      </fill-color-section>

      <field-group label="Position on page">
        <position-controls
            :horizontal="shownAlignment.horizontal"
            :vertical="shownAlignment.vertical"
            @align="onPositionAlign"/>
      </field-group>

      <field-group
          v-if="!isMultiPropertySelection"
          label="Rotate & flip">
        <rotate-flip-controls
            @rotate="(dir) => dir === 'left' ? rotateLeft() : rotateRight()"
            @flip="(dir) => dir === 'horizontal' ? flipHorizontal() : flipVertical()"/>
      </field-group>

      <field-group label="Effects">
        <effect-tiles
            :options="logoEffectOptions"
            :value="shapeEffectValue"
            :columns="3"
            @select="onLogoEffectSelect"/>
        <effect-control-card
            v-if="shapeEffectValue === 'outline'"
            :rows="shapeOutlineRows"
            :color="tempShapeOutlineColor"
            class="lp-logo-panel__effect-card"
            color-aria="Open outline color picker"
            show-color
            @input="onShapeOutlineInput"
            @pick="openImageEffectColorPicker"/>
        <effect-control-card
            v-if="shapeEffectValue === 'shadow'"
            :rows="imageShadowRows"
            :color="tempShapeShadowColor"
            class="lp-logo-panel__effect-card"
            color-aria="Open shadow color picker"
            show-color
            @input="onImageShadowInput"
            @pick="openImageEffectColorPicker"/>
      </field-group>
      <side-popover
          ref="imageEffectColorPopoverEl"
          :open="isColorPickerOpen && (shapeEffectValue === 'outline' || shapeEffectValue === 'shadow')"
          :top="imageEffectColorPopover.top"
          :left="imageEffectColorPopover.left"
          :width="303"
          :title="shapeEffectValue === 'outline' ? 'Outline color' : 'Shadow color'"
          @close="isColorPickerOpen = false"
          @click.native.stop>
        <color-picker
            v-if="shapeEffectValue === 'outline'"
            :color="tempShapeOutlineColor"
            :type="getObjectType"
            :key="'color-picker-stroke-'+((activeObjectForLeftPanel && activeObjectForLeftPanel.Id) || 'none')"
            :selected-objects-list="getSelectedObjectsList"
            :content-list="contentList"
            display-style="inline"
            variant="panel"
            color-type="outline"
            @color-changed="onShapeOutlineColorChanged"
        />
        <color-picker
            v-else
            :active-object="activeObjectForLeftPanel"
            :color="tempShapeShadowColor"
            :type="getObjectType"
            :key="'color-picker-shadow-non-text-object-effect-'+((activeObjectForLeftPanel && activeObjectForLeftPanel.Id) || 'none')"
            :selected-objects-list="getSelectedObjectsList"
            :content-list="contentList"
            :show-gradient="false"
            color-type="shadow"
            display-style="inline"
            variant="panel"
        />
      </side-popover>
    </template>
  </div>
</template>

<script>
import EventBus from '@/EventBus';
import ColorPicker from '@/components/ColorPicker/ColorPicker';
import panelContext from '@/components/LeftSection/mixins/panelContext';
import objectAlignment from '@/components/LeftSection/mixins/objectAlignment';
import objectEffects from '@/components/LeftSection/mixins/objectEffects';
import imageEffects from '@/components/LeftSection/mixins/imageEffects';
import objectTransform from '@/components/LeftSection/mixins/objectTransform';
import gradientFill from '@/components/LeftSection/mixins/gradientFill';
import sidePopover from '@/components/LeftSection/mixins/sidePopover';
import svgOutline from '@/components/LeftSection/mixins/svgOutline';
import FieldGroup from '@/components/LeftSection/ui/FieldGroup';
import SegmentedControl from '@/components/LeftSection/ui/SegmentedControl';
import PositionControls from '@/components/LeftSection/ui/PositionControls';
import RotateFlipControls from '@/components/LeftSection/ui/RotateFlipControls';
import EffectTiles from '@/components/LeftSection/ui/EffectTiles';
import EffectControlCard from '@/components/LeftSection/ui/EffectControlCard';
import SidePopover from '@/components/LeftSection/ui/SidePopover';
import FillColorSection from '@/components/LeftSection/ui/FillColorSection';

const LOGO_EFFECT_OPTIONS = [
  { value: 'none', label: 'None', preview: 'none' },
  { value: 'outline', label: 'Outline', preview: 'outline' },
  { value: 'shadow', label: 'Shadow', preview: 'shadow' },
];

const BLACK = ['#000000', 'rgba(0, 0, 0, 1)'];
const WHITE = ['#FFFFFF', 'rgba(255, 255, 255, 1)'];

export default {
  name:       'LogoPanel',
  components: {
    'color-picker':         ColorPicker,
    'field-group':          FieldGroup,
    'segmented-control':    SegmentedControl,
    'position-controls':    PositionControls,
    'rotate-flip-controls': RotateFlipControls,
    'effect-tiles':         EffectTiles,
    'effect-control-card':  EffectControlCard,
    'side-popover':         SidePopover,
    'fill-color-section':   FillColorSection,
  },
  mixins: [panelContext, objectAlignment, objectEffects, imageEffects, objectTransform, gradientFill, sidePopover, svgOutline],
  props:  {
    kind:        { type: String, default: 'fdic' }, // 'fdic' (FDIC / NCUA) or 'housing' (Equal Housing)
    width:       { type: Number, default: 0 },
    height:      { type: Number, default: 0 },
    productCrop: { type: Object, default: null },
    fillOpen:    { type: Boolean, default: true },
  },
  data() {
    return {
      logoEffectOptions: LOGO_EFFECT_OPTIONS,
      logoColorOptions:  [
        { value: 'Black', label: 'Black' },
        { value: 'White', label: 'White' },
      ],
    };
  },
  computed: {
    // Payload `type` of the old <color-select-button>s (read by activeButton).
    logoType() {
      if (this.kind === 'housing') {
        return 'HOUSING';
      }
      return this.bankType == 'CreditUnion' ? 'NCUA' : 'FDIC';
    },
    // Same check as the old buttons' `selected-button`: a solid black or white fill.
    logoColorValue() {
      const object = this.activeObjectForLeftPanel;
      if (!object || object.isGradient || !object.SvgProperty) {
        return null;
      }
      if (BLACK.indexOf(object.SvgProperty.DefaultColor) !== -1) {
        return 'Black';
      }
      if (WHITE.indexOf(object.SvgProperty.DefaultColor) !== -1) {
        return 'White';
      }
      return null;
    },
  },
  methods: {
    // Old button ids: FDIC 1 / 2, Equal Housing 3 / 4.
    onLogoColorClick(option) {
      const offset = this.kind === 'housing' ? 2 : 0;
      this.activeButton({
        id:    String((option.value === 'Black' ? 1 : 2) + offset),
        title: option.value,
        type:  this.logoType,
      });
    },
    activeButton(object) {
      if (object.type == 'FDIC' || object.type == 'NCUA') {
        EventBus.$emit('changeFdicPhoto', object);
      } else {
        EventBus.$emit('changeHousingPhoto', object.title);
      }
    },
    onLogoEffectSelect(value) {
      // Re-applying the active effect would reset it to its defaults (e.g. the brown shadow).
      if (!this.isMultiPropertySelection && value === this.shapeEffectValue) { return; }
      if (value === 'outline') {
        this.applyOutlineOnShape();
      } else if (value === 'shadow') {
        // The old FDIC multi-select tile called applyShadowOnImage; every other logo tile called applyShadowOnShape.
        if (this.kind === 'fdic' && this.isMultiPropertySelection) {
          this.applyShadowOnImage();
        } else {
          this.applyShadowOnShape();
        }
      } else {
        this.removeShapeEffect();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/lp-tokens';
@import '../styles/lp-panel';

.lp-logo-panel {
  padding: 4px 4px 8px;
}

.lp-logo-panel__effect-card {
  margin-top: 10px;
}
</style>
